import { Component, Inject, SimpleChanges } from '@angular/core';
import {
  DwChildConfigBaseComponent,
  DwMetaDataConfigContext,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryMdAttribute
} from '@devwareapps/devware-cap';

import {
  AviatorMetadataGridRendererConfig,
  AviatorMetadataGridRendererCardConfig
} from '../aviator-metadata-grid-renderer-config.model';

@Component({
  selector: 'app-aviator-metadata-grid-renderer-config',
  templateUrl: './aviator-metadata-grid-renderer-config.component.html'
})
export class AviatorMetadataGridRendererConfigComponent
  extends DwChildConfigBaseComponent<AviatorMetadataGridRendererConfig, DwMetaDataConfigContext> {

  attributes: DwQueryMdAttribute[] = [];
  fieldOptions: { label: string; value: string }[] = [];
  previewEmptyTitle = 'No items found';
  previewEmptyDetail = 'Try adjusting your search or filter criteria.';
  previewEmptyIconClass = 'fa fa-search';

  constructor(
    @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService
  ) {
    super();
  }

  getDefaultConfig(): AviatorMetadataGridRendererConfig {
    return {
      displayMode: 'table',
      emptyTitle: 'No items found',
      emptyDetail: 'Try adjusting your search or filter criteria.',
      emptyIconClass: 'fa fa-search',
      card: {
        useTitleAvatar: false
      },
      cardColumnsDesktop: 3,
      cardColumnsTablet: 2,
      cardColumnsMobile: 1,
      cardGapPx: 16
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this.normalizeAndEmitConfig();
  }

  setupComponent(): void {
    super.setupComponent();
    this.normalizeAndEmitConfig();

    if (!this.configContext?.itemName) {
      return;
    }

    this.dwMetaDataService.getSavedQueryMetaData(this.configContext.itemName, null, this.configContext.metaDataQueryMeaning)
      .subscribe(queryMetaData => {
        this.attributes = queryMetaData?.Attributes || [];
        this.fieldOptions = (this.attributes || []).map(attr => ({
          label: attr.AttributeDisplay || attr.DisplayAttributeName || attr.AttributeName,
          value: attr.AttributeName || attr.DisplayAttributeName
        }));
      });
  }

  onCardFieldChange(fieldKey: 'imageUrlField', value: string): void {
    const nextCardConfig = this.cloneCardConfig();
    nextCardConfig[fieldKey] = value || null;

    this.config = {
      ...this.config,
      card: nextCardConfig
    };
    this.onChange();
  }

  onCardTemplateChange(templateKey: 'titleTemplate' | 'detailTemplate' | 'metaTemplate', value: string): void {
    this.config = {
      ...this.config,
      card: {
        ...this.cloneCardConfig(),
        [templateKey]: value
      }
    };
    this.onChange();
  }

  onChange(): void {
    this.refreshPreviewState();
    this.configChange.emit(this.cloneConfig(this.config));
  }

  private normalizeAndEmitConfig(): void {
    const normalizedConfig = this.normalizeConfig(this.config);
    const originalConfig = this.config || {} as AviatorMetadataGridRendererConfig;

    if (JSON.stringify(originalConfig) !== JSON.stringify(normalizedConfig)) {
      this.config = normalizedConfig;
      this.refreshPreviewState();
      this.onChange();
      return;
    }

    this.config = normalizedConfig;
    this.refreshPreviewState();
  }

  private normalizeConfig(config: AviatorMetadataGridRendererConfig): AviatorMetadataGridRendererConfig {
    const normalizedCard: AviatorMetadataGridRendererCardConfig = {
      useTitleAvatar: false,
      ...(config?.card || {} as AviatorMetadataGridRendererCardConfig)
    };

    normalizedCard.titleTemplate = this.resolveTemplate(normalizedCard.titleTemplate, normalizedCard.titleField);
    normalizedCard.detailTemplate = this.resolveTemplate(normalizedCard.detailTemplate, normalizedCard.detailField);
    normalizedCard.metaTemplate = this.resolveTemplate(normalizedCard.metaTemplate, normalizedCard.metaField);

    return {
      displayMode: config?.displayMode || 'table',
      emptyTitle: config?.emptyTitle || 'No items found',
      emptyDetail: config?.emptyDetail || config?.emptyMessage || 'Try adjusting your search or filter criteria.',
      emptyIconClass: config?.emptyIconClass || 'fa fa-search',
      card: normalizedCard,
      cardColumnsDesktop: config?.cardColumnsDesktop || 3,
      cardColumnsTablet: config?.cardColumnsTablet || 2,
      cardColumnsMobile: config?.cardColumnsMobile || 1,
      cardGapPx: config?.cardGapPx || 16
    };
  }

  private cloneConfig(config: AviatorMetadataGridRendererConfig): AviatorMetadataGridRendererConfig {
    return {
      ...config,
      card: {
        ...(config?.card || {})
      }
    };
  }

  private cloneCardConfig(): AviatorMetadataGridRendererCardConfig {
    return {
      ...(this.config?.card || {})
    };
  }

  private refreshPreviewState(): void {
    this.previewEmptyTitle = this.config?.emptyTitle || 'No items found';
    this.previewEmptyDetail = this.config?.emptyDetail || this.config?.emptyMessage || 'Try adjusting your search or filter criteria.';
    this.previewEmptyIconClass = this.config?.emptyIconClass || 'fa fa-search';
  }

  private resolveTemplate(templateText?: string, fieldName?: string): string {
    const trimmedTemplate = `${templateText || ''}`.trim();
    if (trimmedTemplate) {
      return trimmedTemplate;
    }

    return fieldName ? `[${fieldName}]` : '';
  }
}
